/**
 * API client — thin fetch wrapper around the OpenAPI surface.
 *
 * The access token is set process-wide via setAccessToken() (called from
 * lib/auth.tsx when the OIDC user changes). request() reads the current
 * token via a closure and attaches `Authorization: Bearer <token>`
 * automatically. Callers can still pass an explicit options.token to
 * override the module-level value (handy for tests + ad-hoc tokens).
 *
 * Base URL falls back to "" (same-origin) in the AWS deploy where Caddy
 * reverse-proxies /v1/* to api:8080; for local-dev Arjun lane, set
 * VITE_API_BASE_URL=http://localhost:8080 (real backend) or
 * http://localhost:4010 (Prism mock).
 *
 * See:
 *  - apps/web/src/lib/auth.tsx — sets the access token on every user change
 *  - docs/runbooks/arjun-getting-started.md § Running the UI independently
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

// Module-level token cache, updated by setAccessToken() from auth.tsx.
// Reading via getter (not a closure-captured const) so request() always
// sees the current value.
let currentToken: string | null = null;

export function setAccessToken(token: string | null): void {
  currentToken = token;
}

export function getAccessToken(): string | null {
  return currentToken;
}

// Module-level active-org id, set by the OrgProvider (lib/org-context).
// request() attaches it as the X-Org-Id header so the multi-tenant backend
// scopes the call to that org. null → header omitted (the backend then
// resolves the caller's sole org, the pre-multi-org behaviour).
let currentOrgId: string | null = null;

export function setOrgId(orgId: string | null): void {
  currentOrgId = orgId;
}

export interface ApiError extends Error {
  status: number;
  statusText: string;
  body: unknown;
}

class ApiClientError extends Error implements ApiError {
  status: number;
  statusText: string;
  body: unknown;
  /** Seconds until the server takes the request again (its Retry-After), or null when it said nothing. */
  retryAfterS: number | null;

  constructor(status: number, statusText: string, body: unknown, retryAfterS: number | null = null) {
    super(`${status} ${statusText}`);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.retryAfterS = retryAfterS;
  }
}

/** A Retry-After header as seconds from now: whole seconds, or an HTTP date. Null when absent or unreadable. */
export function parseRetryAfter(value: string | null, nowMs: number = Date.now()): number | null {
  if (!value) return null;
  const v = value.trim();
  if (/^\d+$/.test(v)) return Number(v);
  const at = Date.parse(v);
  return Number.isNaN(at) ? null : Math.max(0, Math.round((at - nowMs) / 1000));
}

/**
 * A request that got no answer: fetch itself rejected (offline, DNS or CORS, a connection reset, the web container
 * being recreated), and the browser's TypeError is kept as the cause. Marked here, where fetch is called, so a page
 * can tell it from a TypeError of its own code after an answer arrived (#868 review). It stays a TypeError, which
 * lib/api/error.ts's isNetworkError and older callers key on.
 */
export class NetworkError extends TypeError {
  constructor(cause: unknown) {
    super(cause instanceof Error && cause.message ? cause.message : "The request got no answer", { cause });
    this.name = "NetworkError";
  }
}

/** fetch, with its own rejection marked as a NetworkError. An aborted request stays as it is. */
async function fetchAnswer(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new NetworkError(err);
  }
}

async function errorOf(response: Response): Promise<ApiClientError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = await response.text().catch(() => null);
  }
  return new ApiClientError(response.status, response.statusText, body,
    parseRetryAfter(response.headers?.get?.("Retry-After") ?? null));
}

async function request<T>(
  method: string,
  path: string,
  options?: { body?: unknown; token?: string; orgId?: string },
): Promise<T> {
  // Empty base URL → use a relative URL so the browser resolves against
  // its current origin. Avoids the URL constructor's "Invalid URL" when
  // path is relative and base is "".
  const url = API_BASE_URL ? new URL(path, API_BASE_URL).toString() : path;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (options?.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  // Explicit per-call token wins over the module-level one. This lets
  // tests + first-class callers override without globally mutating
  // currentToken.
  const token = options?.token ?? currentToken;
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // Multi-tenancy: scope the request to the active org. Omitted when unset
  // (backend falls back to the caller's sole membership). An explicit per-call
  // orgId wins: a route whose path names an org the backend must see as the
  // X-Org-Id too (the members list, read for another org in the platform console).
  const orgId = options?.orgId ?? currentOrgId;
  if (orgId) {
    headers["X-Org-Id"] = orgId;
  }

  const response = await fetchAnswer(url, {
    method,
    headers,
    body: options?.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    throw await errorOf(response);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

// Multipart POST (file upload). Distinct from request() because the browser
// MUST set Content-Type itself (it appends the multipart boundary), so we never
// add the JSON Content-Type here. Auth + active-org headers still ride along.
async function requestForm<T>(path: string, form: FormData): Promise<T> {
  const url = API_BASE_URL ? new URL(path, API_BASE_URL).toString() : path;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (currentToken) {
    headers["Authorization"] = `Bearer ${currentToken}`;
  }
  if (currentOrgId) {
    headers["X-Org-Id"] = currentOrgId;
  }
  const response = await fetchAnswer(url, { method: "POST", headers, body: form });
  if (!response.ok) {
    throw await errorOf(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// Raw-body POST: one file sent as it is (an agent's pendrive bundle). Distinct from request() because the body is
// binary, so it is never JSON-encoded and the Content-Type is the caller's (application/octet-stream by default); and
// from requestForm() because the server reads the body itself, not a multipart field. Auth + active-org headers ride along.
async function requestRaw<T>(path: string, body: Blob | ArrayBuffer, contentType: string): Promise<T> {
  const url = API_BASE_URL ? new URL(path, API_BASE_URL).toString() : path;
  const headers: Record<string, string> = { Accept: "application/json", "Content-Type": contentType };
  if (currentToken) {
    headers["Authorization"] = `Bearer ${currentToken}`;
  }
  if (currentOrgId) {
    headers["X-Org-Id"] = currentOrgId;
  }
  const response = await fetchAnswer(url, { method: "POST", headers, body });
  if (!response.ok) {
    throw await errorOf(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

// Authenticated binary GET — fetches a file (e.g. a stored report artifact)
// as a Blob with the auth + active-org headers. Distinct from request() because
// the body is binary, not JSON, so we never set Accept: application/json.
async function requestBlob(path: string): Promise<Blob> {
  const url = API_BASE_URL ? new URL(path, API_BASE_URL).toString() : path;
  const headers: Record<string, string> = {};
  if (currentToken) {
    headers["Authorization"] = `Bearer ${currentToken}`;
  }
  if (currentOrgId) {
    headers["X-Org-Id"] = currentOrgId;
  }
  const response = await fetchAnswer(url, { method: "GET", headers });
  if (!response.ok) {
    throw await errorOf(response);
  }
  return response.blob();
}

export const api = {
  get: <T>(path: string, options?: { token?: string; orgId?: string }) =>
    request<T>("GET", path, options),
  /** Authenticated binary GET → Blob (file downloads). */
  getBlob: (path: string) => requestBlob(path),
  post: <T>(path: string, body?: unknown, options?: { token?: string }) =>
    request<T>("POST", path, { body, token: options?.token }),
  /** Multipart POST for file uploads (the browser sets the boundary). */
  postForm: <T>(path: string, form: FormData) => requestForm<T>(path, form),
  /** Raw-body POST: a file sent as it is, not JSON and not multipart (default Content-Type application/octet-stream). */
  postRaw: <T>(path: string, body: Blob | ArrayBuffer, contentType = "application/octet-stream") =>
    requestRaw<T>(path, body, contentType),
  patch: <T>(path: string, body?: unknown, options?: { token?: string }) =>
    request<T>("PATCH", path, { body, token: options?.token }),
  put: <T>(path: string, body?: unknown, options?: { token?: string }) =>
    request<T>("PUT", path, { body, token: options?.token }),
  delete: <T>(path: string, options?: { token?: string }) =>
    request<T>("DELETE", path, options),
};

export { ApiClientError };
export const apiBaseUrl = API_BASE_URL;
